import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, RefreshCw, BarChart3, Edit3, Zap } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { authHeaders as getAuthHeaders } from '../../utils/unifiedAuth';
import api from '../../utils/api';

const STAT_KEYS = ['providers', 'patients', 'countries', 'treatments'];
const STAT_LABELS = {
  providers: 'Verified Clinics',
  patients: 'Happy Patients',
  countries: 'Countries',
  treatments: 'Treatments Booked',
};

const AdminLandingStats = ({ embedded = false }) => {
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_BACKEND_URL || '';

  const [config, setConfig] = useState(null);
  const [liveStats, setLiveStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [recomputing, setRecomputing] = useState(false);
  const [message, setMessage] = useState(null);

  const hdrs = useCallback(() => getAuthHeaders(), []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [configRes, statsRes] = await Promise.all([
        api.get(`/admin/platform-stats/config`),
        api.get(`/admin/platform-stats?force_refresh=true`),
      ]);

      setConfig(configRes.data.config);
      setLiveStats(statsRes.data.stats);
    } catch (err) {
      setMessage({ type: 'error', text: `Failed to load: ${err.message}` });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleModeToggle = (statKey) => {
    setConfig(prev => {
      if (!prev || !prev[statKey]) return prev;
      return {
        ...prev,
        [statKey]: {
          ...prev[statKey],
          mode: prev[statKey].mode === 'manual' ? 'auto' : 'manual',
        },
      };
    });
  };

  const handleValueChange = (statKey, field, value) => {
    setConfig(prev => {
      if (!prev || !prev[statKey]) return prev;
      return {
        ...prev,
        [statKey]: {
          ...prev[statKey],
          [field]: field === 'manual_value' ? parseInt(value) || 0 : value,
        },
      };
    });
  };

  const handleSave = async () => {
    if (!config) {
      setMessage({ type: 'error', text: 'Configuration not loaded yet. Please wait.' });
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      const payload = {};
      for (const key of STAT_KEYS) {
        if (config?.[key]) {
          payload[key] = {
            mode: config[key].mode,
            manual_value: config[key].manual_value,
            // Clear display_format when switching to auto so computed value is shown
            display_format: config[key].mode === 'auto' ? null : (config[key].display_format || null),
            rounding_strategy: config[key].rounding_strategy,
            label: config[key].label,
          };
        }
      }

      const res = await api.post(`/admin/platform-stats/config`, payload);
      setMessage({ type: 'success', text: 'Stats configuration saved. Landing page will update on next load.' });
      fetchData();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  const handleRecompute = async () => {
    setRecomputing(true);
    try {
      const res = await api.post(`/admin/platform-stats/recompute`);
      setMessage({ type: 'success', text: 'Stats recomputed from database.' });
      fetchData();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setRecomputing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
        <div className="text-gray-500">Loading stats configuration...</div>
      </div>
    );
  }

  return (
    <div className={embedded ? '' : 'min-h-screen bg-gray-50'} data-testid="admin-landing-stats-page">
      <div className={embedded ? '' : 'max-w-4xl mx-auto p-6'}>
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            {!embedded && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/treatx-admin/dashboard')}
                data-testid="back-to-dashboard-btn"
              >
                <ArrowLeft className="h-4 w-4 mr-1" /> Back
              </Button>
            )}
            {!embedded && (
              <div>
                <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Landing Page Stats
                </h1>
                <p className="text-sm text-gray-500">
                  Control numbers displayed on the public landing page
                </p>
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRecompute}
              disabled={recomputing}
              data-testid="recompute-stats-btn"
            >
              <RefreshCw className={`h-4 w-4 mr-1 ${recomputing ? 'animate-spin' : ''}`} />
              {recomputing ? 'Recomputing...' : 'Recompute Live'}
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={saving}
              data-testid="save-stats-btn"
            >
              <Save className="h-4 w-4 mr-1" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>

        {/* Message */}
        {message && (
          <div
            className={`mb-4 p-3 rounded text-sm ${
              message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'
            }`}
            data-testid="stats-message"
          >
            {message.text}
          </div>
        )}

        {/* Info box */}
        <Card className="mb-6 border-blue-200 bg-blue-50">
          <CardContent className="p-4">
            <p className="text-sm text-blue-800">
              <strong>How it works:</strong> Each stat can be in <strong>Auto</strong> mode (computed live from DB) or <strong>Manual</strong> mode (you set the number).
              Manual mode is ideal for early launch when real counts are low. The landing page always fetches from the API — no hardcoded numbers.
            </p>
          </CardContent>
        </Card>

        {/* Stat Cards */}
        <div className="space-y-4">
          {STAT_KEYS.map((key) => {
            const cfg = config?.[key] || {};
            const live = liveStats?.[key] || {};
            const isManual = cfg.mode === 'manual';

            return (
              <Card key={key} className={isManual ? 'border-amber-300' : 'border-gray-200'} data-testid={`stat-card-${key}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base font-semibold flex items-center gap-2">
                      {isManual ? <Edit3 className="h-4 w-4 text-amber-500" /> : <Zap className="h-4 w-4 text-green-500" />}
                      {STAT_LABELS[key]}
                    </CardTitle>
                    <Button
                      variant={isManual ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => handleModeToggle(key)}
                      className={isManual ? 'bg-amber-500 hover:bg-amber-600 text-white' : ''}
                      data-testid={`toggle-mode-${key}`}
                    >
                      {isManual ? 'Manual' : 'Auto'}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4">
                    {/* Live DB value */}
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">Live DB Value</label>
                      <div className="text-lg font-mono font-bold text-gray-700" data-testid={`live-value-${key}`}>
                        {live.value ?? '–'}
                      </div>
                    </div>

                    {/* Manual override */}
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">Manual Override</label>
                      <Input
                        type="number"
                        value={cfg.manual_value || 0}
                        onChange={(e) => handleValueChange(key, 'manual_value', e.target.value)}
                        disabled={!isManual}
                        className={!isManual ? 'opacity-50' : ''}
                        data-testid={`manual-value-${key}`}
                      />
                    </div>

                    {/* Display format override */}
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">Display Format</label>
                      <Input
                        type="text"
                        placeholder="e.g. 500+"
                        value={cfg.display_format || ''}
                        onChange={(e) => handleValueChange(key, 'display_format', e.target.value || null)}
                        disabled={!isManual}
                        className={!isManual ? 'opacity-50' : ''}
                        data-testid={`display-format-${key}`}
                      />
                    </div>
                  </div>

                  {/* Current display */}
                  <div className="mt-3 pt-3 border-t flex items-center justify-between">
                    <span className="text-xs text-gray-400">Currently showing on landing page:</span>
                    <span className="font-bold text-lg" data-testid={`current-display-${key}`}>
                      {live.display || '–'}
                    </span>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AdminLandingStats;
